import { useCallback, useRef } from 'react';

/**
 * Detect press-and-hold (long-press) gestures on touch devices.
 * Fires `callback` after `thresholdMs`. Cancels on touchmove beyond
 * `deadzonePx`, on touchend before threshold, or on touchcancel.
 *
 * Returns handlers to spread onto a touch target plus a `didLongPress`
 * ref the consumer can read in onClick to suppress click-after-long-press.
 */
export function useLongPress(callback, { thresholdMs = 500, deadzonePx = 8 } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const onTouchStart = useCallback((e) => {
    firedRef.current = false;
    const t = e.touches?.[0];
    if (t) startRef.current = { x: t.clientX, y: t.clientY };
    cancel();
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      callback();
    }, thresholdMs);
  }, [callback, thresholdMs, cancel]);

  const onTouchMove = useCallback((e) => {
    const t = e.touches?.[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - startRef.current.x);
    const dy = Math.abs(t.clientY - startRef.current.y);
    if (dx > deadzonePx || dy > deadzonePx) cancel();
  }, [cancel, deadzonePx]);

  const onTouchEnd = cancel;
  const onTouchCancel = cancel;

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, didLongPress: firedRef };
}

export default useLongPress;
